import { Button, ButtonGroup, Box, HStack, Icon, IconButton, Text, VStack } from '@chakra-ui/react';
import { useState } from 'react';
import { t } from '@lingui/core/macro';
import { User, Users, X } from 'lucide-react';
import { TeamBulkUsers } from '@duke-hq/app-provider-web/tenant/Team/TeamBulkUsers/TeamBulkUsers';
import { TeamIndividualUser } from '@duke-hq/app-provider-web/tenant/Team/TeamBulkUsers/TeamIndividualUser';
import type { TeamIndividualUserFormData } from '@duke-hq/app-provider-web/tenant/Team/TeamBulkUsers/TeamIndividualUserForm';
import { iconSize } from '@duke-hq/ui/constants/scalable';

function TeamMemberHeader({
  member,
  mode,
  setMode,
  onClose,
}: {
  member?: TeamIndividualUserFormData;
  mode: 'individual' | 'bulk';
  setMode: (mode: 'individual' | 'bulk') => void;
  onClose?: () => void;
}) {
  return (
    <HStack justifyContent="space-between" w="full" align="center">
      <Text fontSize="md" fontWeight="bold">
        {member ? t`Update team member` : t`Invite team member`}
      </Text>

      <HStack spacing={2}>
        <ButtonGroup
          alignItems="center"
          bg="bg.muted"
          borderRadius="md"
          size="sm"
          spacing={0}
          p={1}
          overflow="hidden"
        >
          <Button
            onClick={() => setMode('individual')}
            leftIcon={<Icon as={User} boxSize={iconSize.md} />}
            variant="ghost"
            bg={mode === 'individual' ? 'white' : 'transparent'}
            color={mode === 'individual' ? 'brand.600' : 'fg.muted'}
            borderRadius="md"
            _hover={{ bg: mode === 'individual' ? 'white' : 'gray.100' }}
          >
            Individual
          </Button>

          <Button
            onClick={() => setMode('bulk')}
            leftIcon={<Icon as={Users} boxSize={iconSize.md} />}
            variant="ghost"
            bg={mode === 'bulk' ? 'white' : 'transparent'}
            color={mode === 'bulk' ? 'brand.600' : 'fg.muted'}
            borderRadius="md"
            _hover={{ bg: mode === 'bulk' ? 'white' : 'gray.100' }}
          >
            Bulk
          </Button>
        </ButtonGroup>

        <IconButton
          onClick={onClose}
          aria-label="Close"
          size="sm"
          variant="ghost"
          icon={<X size={iconSize.lg} />}
        />
      </HStack>
    </HStack>
  );
}

export function TeamMemberInternalEnrolmentForm({
  member,
  onClose,
}: {
  member?: TeamIndividualUserFormData;
  onClose?: () => void;
}): React.ReactElement {
  const [mode, setMode] = useState<'individual' | 'bulk'>('individual');

  return (
    <VStack spacing={0} align="stretch">
      <Box w="full" px={6}>
        <TeamMemberHeader member={member} mode={mode} setMode={setMode} onClose={onClose} />
      </Box>
      <Box w="full" h="auto">
        {mode === 'individual' ? (
          <TeamIndividualUser member={member} onClose={onClose} />
        ) : (
          <TeamBulkUsers onClose={onClose} />
        )}
      </Box>
    </VStack>
  );
}
